import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCounts,
  useStartCount,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useActiveCompany } from "@/lib/company-context";
import {
  COUNT_STATUS_LABEL,
  COUNT_STATUS_TONE,
  fmtDate,
} from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/conteos/",
)({
  component: ConteosIndexPage,
});

function ConteosIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ConteosIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function ConteosIndexContent() {
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const countsQuery = useCounts(companyId);
  const warehousesQuery = useWarehouses(companyId);
  const startCount = useStartCount(companyId);

  const [showStart, setShowStart] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [notes, setNotes] = useState("");

  const counts = countsQuery.data?.counts ?? [];
  const summary = {
    open: counts.filter((c) => c.status === "draft" || c.status === "in_progress").length,
    posted: counts.filter((c) => c.status === "posted").length,
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/inventario"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a Inventario
          </Link>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Conteos físicos
            </h1>
          </div>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Toma de inventario en una bodega. Al postear, las diferencias frente
            al saldo del sistema se ajustan automáticamente con sus respectivos
            asientos contables (4250 sobrantes, 5310 faltantes).
          </p>
        </div>
        <Button onClick={() => setShowStart((s) => !s)}>
          <Plus className="h-4 w-4" />
          {showStart ? "Cerrar" : "Iniciar conteo"}
        </Button>
      </header>

      {summary.open + summary.posted > 0 && (
        <div className="flex flex-wrap gap-2 text-sm">
          {summary.open > 0 && (
            <Badge variant="warning">
              {summary.open} en curso
            </Badge>
          )}
          {summary.posted > 0 && (
            <Badge variant="success">{summary.posted} posteados</Badge>
          )}
        </div>
      )}

      {showStart && (
        <Card>
          <CardContent className="flex flex-col gap-3 py-4">
            <div>
              <Label>Bodega a contar</Label>
              <select
                className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
              >
                <option value="">— Selecciona —</option>
                {(warehousesQuery.data?.warehouses ?? [])
                  .filter((w) => w.isActive)
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} — {w.name}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Se incluirán todos los productos stockables que tengan saldo
                actual en esta bodega. Los movimientos del sistema siguen
                aplicándose mientras el conteo está abierto; la varianza se
                calcula contra el saldo en vivo al postear.
              </p>
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Conteo de cierre de mes"
                maxLength={1000}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowStart(false)}>
                Cancelar
              </Button>
              <Button
                disabled={startCount.isPending || !warehouseId}
                onClick={() => {
                  startCount.mutate(
                    { warehouseId, notes: notes || undefined },
                    {
                      onSuccess: (r) => {
                        toast.success(
                          `Conteo iniciado con ${r.lineCount} producto(s).`,
                        );
                        void navigate({
                          to: "/companies/$companyId/inventario/conteos/$countId",
                          params: { companyId, countId: r.countId },
                        });
                      },
                      onError: (err) =>
                        toast.error("No pudimos iniciar el conteo", {
                          description: err.message,
                        }),
                    },
                  );
                }}
              >
                {startCount.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Iniciar conteo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {countsQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : countsQuery.isError ? (
        <ErrorState
          title="No pudimos cargar los conteos"
          description={countsQuery.error?.message ?? ""}
          onRetry={() => void countsQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : counts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <ClipboardCheck className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Aún no hay conteos</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Inicia el primero para reconciliar bodega vs. sistema.
              </p>
            </div>
            <Button onClick={() => setShowStart(true)}>
              <Plus className="h-4 w-4" />
              Iniciar conteo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Iniciado</TableHead>
                <TableHead>Bodega</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Posteado</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {counts.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {fmtDate(c.startedAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted" className="font-mono">
                      {c.warehouseCode ?? "?"}
                    </Badge>
                    <span className="ml-2 text-sm text-muted-foreground">
                      {c.warehouseName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={COUNT_STATUS_TONE[c.status]}>
                      {COUNT_STATUS_LABEL[c.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.postedAt ? fmtDate(c.postedAt) : "—"}
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                    {c.notes ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="ghost">
                      <Link
                        to="/companies/$companyId/inventario/conteos/$countId"
                        params={{ companyId, countId: c.id }}
                      >
                        Abrir
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
